import Home from './Home';
import Sidebar from './Sidebar';
import AlbumPage from "./Album/AlbumPage";
import TravelPage from "./Travel/TravelPage";
import PlacePage from "./Place/PlacePage";
import HikingPage from "./Hiking/HikingPage";
import NotFoundPage from "./NotFound/NotFoundPage";
import PrivacyPage from "./OpenPeaks/PrivacyPage";
import MaintenancePage from "./Maintenance/MaintenancePage";
import PageTracker from "./Utils/PageTracker";
import '@fontsource-variable/instrument-sans';
import './Static/css/index.css';
// Finitions (police, accent, cartes) par-dessus index.css
import './Static/css/theme.css';
import './Static/css/hiking.css';
import './Static/css/travel.css';
// Mode sombre des pages hors accueil, en dernier
import './Static/css/dark.css';
import { lazy, Suspense, useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useConfig, getConfigValue } from './Utils/config';
import { useDarkMode } from './Utils/theme';
import { loadOrReload, PageLoadBoundary } from './Utils/lazyPage';

// La carte (MapLibre, lourd) n'est chargée qu'en ouvrant sa page
const HikingMap = lazy(() => loadOrReload(() => import('./Hiking/HikingMap')));

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Mode sombre : réglé depuis l'accueil, appliqué à tout le site
  const [darkMode, setDarkMode] = useDarkMode();
  const sidebarRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const { config, error } = useConfig();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Fermer la sidebar en cliquant ailleurs sur l'écran
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target)
      ) {
        closeSidebar();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [sidebarOpen]);

  if (error) {
    console.error('Erreur lors du chargement de la configuration:', error);
  }

  const backgroundImageValue = getConfigValue(config, "HOME_BACKGROUND_IMAGE");

  // MAINTENANCE_MODE = 1 dans la table config : tout le site affiche
  // l'écran de maintenance. Absent ou 0 : le site s'affiche normalement.
  const maintenanceMode = String(getConfigValue(config, "MAINTENANCE_MODE") ?? "").trim() === "1";

  if (maintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <Router>
      {/* Statistiques de visite (admin). Seulement une fois la config
          reçue (ou en échec) : avant, MAINTENANCE_MODE est encore inconnu
          et une visite pendant la maintenance serait comptée. */}
      {(config || error) && <PageTracker />}
      <div className="App">
        <Routes>
          <Route path="/album/:albumId" element={<AlbumPage />} />
          <Route path="/travel/:countryId" element={<TravelPage />} />
          <Route path="/travel/:countryId/place/:placeId" element={<PlacePage />} />
          <Route path="/hiking/:hikingId" element={<HikingPage />} />
          <Route path="/randonnees/carte" element={
            <PageLoadBoundary>
              <Suspense fallback={<div className="hike-map-page" />}>
                <HikingMap />
              </Suspense>
            </PageLoadBoundary>
          } />
          <Route path="/openpeaks/privacy" element={<PrivacyPage />} />
          <Route path="/" element={
            <div className={darkMode ? 'home-dark-mode' : ''}>
              <button className="mobile-toggle" onClick={toggleSidebar} ref={toggleButtonRef}>
                ☰
              </button>
              <div className={`sidebar ${sidebarOpen ? 'open' : ''}`} ref={sidebarRef}>
                  <Sidebar 
                    profilPicture={getConfigValue(config, "PROFIL_PICTURE")}
                    showDarkModeToggle={true}
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                  />
              </div>
              <div className="content">
                <Home
                  backgroundImage={backgroundImageValue}
                  backgroundPosition={getConfigValue(config, "HOME_BACKGROUND_POSITION")}
                  darkMode={darkMode}
                  setDarkMode={setDarkMode}
                />
              </div>
            </div>
          } />
          {/* Route 404 - doit être en dernière position */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
